import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { WifiIcon, AlertTriangle, CheckCircle2, Globe, RefreshCw, Power } from "lucide-react";

/**
 * Remote Sessions admin panel — mounted as a third tab inside the AI
 * Office Manager. Surfaces:
 *   - Master toggle (mirrors Company Settings)
 *   - Active-session count + unusual-flag count
 *   - Login events table with IP → city/country + flag / acknowledge
 */
export default function RemoteSessionsPanel() {
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [unusualOnly, setUnusualOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/remote/settings"),
        api.get(`/remote/login-events?unusual_only=${unusualOnly ? "true" : "false"}&limit=100`),
      ]);
      setSettings(s.data);
      setEvents(e.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [unusualOnly]);

  const toggle = async () => {
    setToggling(true);
    try {
      const { data } = await api.patch("/remote/settings", { remote_tracking_enabled: !settings.remote_tracking_enabled });
      setSettings((s) => ({ ...s, remote_tracking_enabled: !!data.remote_tracking_enabled }));
      toast.success(data.remote_tracking_enabled ? "Remote tracking enabled" : "Remote tracking disabled");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setToggling(false);
  };

  const ack = async (id) => {
    try {
      await api.post(`/remote/login-events/${id}/acknowledge`);
      setEvents((rows) => rows.map((r) => r.id === id ? { ...r, acknowledged: true } : r));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/remote/sweep-stale");
      toast.success(`Sweep complete · ${data.closed} stale session(s) closed`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSweeping(false);
  };

  if (loading) return <div className="font-mono text-xs text-muted-foreground">Loading remote sessions…</div>;

  return (
    <div>
      <div className="overline mb-2 flex items-center gap-2"><WifiIcon className="w-4 h-4" /> § REMOTE WORK ENGINE</div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading font-black text-3xl tracking-tight" data-testid="remote-sessions-title">Remote Sessions</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Live view of employees signed in from an approved remote worksite. IP geolocation flags anomalies for review.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="remote-sweep"
            onClick={sweep}
            disabled={sweeping}
            className="border-2 border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2 hover:bg-secondary disabled:opacity-40"
          ><Power className="w-3 h-3" /> {sweeping ? "Sweeping…" : "Sweep Stale"}</button>
          <button
            data-testid="remote-refresh"
            onClick={load}
            className="border-2 border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2 hover:bg-secondary"
          ><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>

      {/* Master toggle */}
      <div className={"hard-card p-5 mb-6 " + (settings.remote_tracking_enabled ? "bg-good/10 border-good" : "bg-warn/10")}
           data-testid="remote-master-toggle-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="overline mb-1">Master Toggle</div>
            <div className="font-heading font-black text-2xl">
              Remote Tracking is <span data-testid="remote-master-state">{settings.remote_tracking_enabled ? "ON" : "OFF"}</span>
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-2 max-w-xl">
              {settings.remote_tracking_enabled
                ? "Portal logins from employees on a Remote worksite auto clock-in, ping every 15 min, and log IP → city/country. Flip OFF to pause all remote-side effects immediately."
                : "Dormant. No login triggers auto clock-in, no heartbeats, no IP mapping. Flip ON to activate — every employee on a Remote worksite must accept the on-screen disclosure before hours are counted."}
            </div>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="remote-master-toggle"
              disabled={toggling}
              checked={!!settings.remote_tracking_enabled}
              onChange={toggle}
              className="w-6 h-6 accent-ink"
            />
            <span className="font-mono text-[11px] uppercase tracking-widest">{toggling ? "…" : (settings.remote_tracking_enabled ? "Enabled" : "Disabled")}</span>
          </label>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KPI icon={WifiIcon} label="Active Remote Sessions" value={settings.active_remote_sessions} testid="kpi-active-sessions" />
        <KPI icon={AlertTriangle} label="Unusual Logins (unread)" value={settings.unusual_flags_unread}
             highlight={settings.unusual_flags_unread > 0} testid="kpi-unusual" />
        <KPI icon={Globe} label="Heartbeat Interval"
             value={`${Math.round(settings.heartbeat_interval_sec / 60)} min`} testid="kpi-heartbeat" />
      </div>

      {/* Events table */}
      <div className="flex items-center gap-3 mb-3">
        <div className="overline">Login Events</div>
        <label className="inline-flex items-center gap-2 cursor-pointer font-mono text-[11px]">
          <input
            type="checkbox"
            data-testid="filter-unusual-only"
            checked={unusualOnly}
            onChange={(e) => setUnusualOnly(e.target.checked)}
            className="accent-ink"
          />
          Show only unusual
        </label>
      </div>
      {events.length === 0 ? (
        <div className="hard-card p-5 font-mono text-xs text-muted-foreground" data-testid="events-empty">
          No login events {unusualOnly ? "flagged as unusual " : ""}yet.
        </div>
      ) : (
        <div className="hard-card overflow-x-auto">
          <table className="w-full font-mono text-sm" data-testid="events-table">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">When</th>
                <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">User</th>
                <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">IP</th>
                <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Location</th>
                <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Flag</th>
                <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-ink/20" data-testid={`event-row-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(e.logged_in_at).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{e.email}</div>
                    <div className="text-[10px] text-muted-foreground">{e.role}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{e.ip}</td>
                  <td className="px-3 py-2 text-xs">
                    {e.geo?.city ? `${e.geo.city}, ${e.geo.region || ""}` : "—"}
                    <div className="text-[10px] text-muted-foreground">{e.geo?.country || (e.geo?.private ? "private network" : "")}</div>
                  </td>
                  <td className="px-3 py-2">
                    {e.unusual ? (
                      <span className="px-2 py-1 border-2 border-bad bg-bad/10 text-bad font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> unusual
                      </span>
                    ) : (
                      <span className="px-2 py-1 border-2 border-ink/20 font-mono text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> normal
                      </span>
                    )}
                    {e.unusual_reason && <div className="text-[10px] text-muted-foreground mt-1">{e.unusual_reason}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {e.unusual && !e.acknowledged && (
                      <button
                        data-testid={`ack-${e.id}`}
                        onClick={() => ack(e.id)}
                        className="border-2 border-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-1"
                      ><CheckCircle2 className="w-3 h-3" /> Acknowledge</button>
                    )}
                    {e.acknowledged && (
                      <span className="text-[10px] font-mono text-muted-foreground">acknowledged</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div className={"hard-card p-4 " + (highlight ? "bg-bad/10 border-bad" : "")} data-testid={testid}>
      <div className="overline mb-2 flex items-center gap-2"><Icon className="w-4 h-4" /> {label}</div>
      <div className={"font-heading font-black " + (highlight ? "text-3xl text-bad" : "text-2xl")}>{value}</div>
    </div>
  );
}
